import {AttrsSchema, Component, jsx} from 'panel';

import {formatPercent} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {LENGTH, SIZE, TextHighlightEl, THEME} from 'common/components/text-highlight';
import {TooltipEl} from 'common/components/tooltip';
import {DisplaySize} from 'common/components/tooltip/types';
import {LiftType} from 'common/report/util/lift/types';

import css from './index.inline.styl';

export enum MetricChartSize {
  Small = `small`,
  Large = `large`,
}

export interface MetricLiftContext {
  liftType: LiftType;
  comparisonValue: string;
  liftComparisonValue: number;
}

export interface MetricChartAttributes {
  'comparison-value'?: number;
  label?: string;
  value: number;
  'display-value'?: string;
  'sub-label'?: string;
  size?: MetricChartSize;
  color?: string;
  'is-centered'?: boolean;
  'value-tooltip'?: string;
  'hide-tooltip'?: boolean;
  'lift-context'?: MetricLiftContext;
}

export class MetricChart extends Component<EmptyObj, MetricChartAttributes> {
  static get attrsSchema(): AttrsSchema<MetricChartAttributes> {
    return {
      ...super.attrsSchema,
      'comparison-value': `number`,
      label: `string`,
      value: `number`,
      'display-value': {type: `string`, description: `formatted value to display.`},
      'hide-tooltip': `boolean`,
      'value-tooltip': `string`,
      'sub-label': `string`,
      size: {type: `string`, enum: Object.values(MetricChartSize), default: MetricChartSize.Large},
      color: `string`,
      'is-centered': `boolean`,
      'lift-context': `json`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
    };
  }

  renderTemplate() {
    return (
      <div
        sel='.root'
        class={{
          [`size-${this.attr(`size`)}`]: true,
          centered: this.attr(`is-centered`),
        }}
      >
        <div sel='.segment-name-container'>
          <div sel='.segment-name'>
            {this.attr(`color`) && <div sel='.segment-color' style={{'background-color': this.attr(`color`)}} />}
            <div sel='.segment-label' attrs={{elref: `label`}}>
              {this.attr(`label`)}
            </div>
            {!this.attr(`hide-tooltip`) && (
              <TooltipEl attrs={{size: DisplaySize.Medium}}>{this.attr(`label`)}</TooltipEl>
            )}
          </div>
        </div>
        {this.renderMainValue()}

        {this.renderSubValue()}
      </div>
    );
  }

  renderMainValue() {
    let displayedValue = this.attr(`display-value`);
    let color = null;

    if (this.attr(`lift-context`)) {
      const {liftType, liftComparisonValue} = this.attr(`lift-context`);
      let addPositive = false;

      // overall isn't a comparison so it doesn't get + or coloring
      if (liftType !== LiftType.Overall) {
        if (liftComparisonValue !== 0) {
          color = liftComparisonValue > 0 ? `positive` : `negative`;
        }

        if (liftComparisonValue > 0) {
          addPositive = true;
        }
      }

      displayedValue = `${addPositive ? `+` : ``}${formatPercent(liftComparisonValue)}`;
    }

    return (
      <div sel='.value-wrapper'>
        {!this.attr(`hide-tooltip`) && this.attr(`value-tooltip`) && (
          <TooltipEl attrs={{elref: `valueTooltip`}}>{this.attr(`value-tooltip`)}</TooltipEl>
        )}
        <div sel='.value' class={{[color]: color}} attrs={{elref: `value`}}>
          {displayedValue}
        </div>
      </div>
    );
  }

  renderSubValue() {
    if (this.attr(`lift-context`)) {
      return this.renderLiftSubValue();
    } else if (this.attr(`comparison-value`)) {
      // time comparison
      return this.renderComparison();
    }

    return (
      <>
        {this.attr(`sub-label`) && (
          <div sel='.sub-label' attrs={{elref: `subLabel`}}>
            {this.attr(`sub-label`)}
          </div>
        )}
      </>
    );
  }

  renderLiftSubValue() {
    const {liftType, comparisonValue} = this.attr(`lift-context`);
    const segmentedValue = this.attr(`display-value`);
    const comparisonCopy = liftType !== LiftType.Overall ? ` compared to ` : ` / `;

    return (
      <>
        <div sel='.subvalue-row'>
          <TextHighlightEl
            attrs={{
              theme: THEME.NEUTRAL,
              text: segmentedValue,
            }}
          />
          {comparisonCopy}
          <TextHighlightEl
            attrs={{
              theme: THEME.NEUTRAL,
              text: comparisonValue,
            }}
          />
        </div>
        {this.attr(`sub-label`) && (
          <div sel='.sub-label' attrs={{elref: `subLabel`}}>
            {this.attr(`sub-label`)}
          </div>
        )}
      </>
    );
  }

  renderComparison() {
    const comparisonValue: number = this.attr(`comparison-value`);
    const percentChange = (this.attr(`value`) - comparisonValue) / comparisonValue;
    const percentChangePositive = percentChange > 0;

    return (
      <>
        {this.attr(`sub-label`) && (
          <div sel='.sub-label' attrs={{elref: `subLabel`}}>
            {this.attr(`sub-label`)}
          </div>
        )}
        <div sel='.change-row'>
          <TextHighlightEl
            attrs={{
              elref: `comparisonValue`,
              text: formatPercent(Math.abs(percentChange)),
              icon: percentChangePositive ? `arrow-up` : `arrow-down`,
              theme: percentChangePositive ? THEME.POSITIVE : THEME.NEGATIVE,
              size: SIZE.SMALL,
              length: LENGTH.WIDE,
            }}
          />
        </div>
      </>
    );
  }
}

interface MetricChartJsxProps {
  attrs: MetricChartAttributes;
}

export const MetricChartEl = defineAndMakeJsxFactory<MetricChartJsxProps>(`mp-metric`, MetricChart);
export const MetricChartReact = panel2react<MetricChartJsxProps>(`mp-metric`, MetricChart);
